import {
  Badge,
  Box,
  Button,
  Card,
  Field,
  Flex,
  HStack,
  Heading,
  InfoBox,
  Input,
  RefreshButton,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  notify,
} from "components/ui";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "components/ui/dialog";
import DeleteConfirmDialog from "components/common/DeleteConfirmDialog";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import { MdDelete, MdEdit, MdHistory, MdPerson } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  CustomerCompanyWorkflow,
  CustomerCompanyWorkflowLog,
  WorkflowExecutionStatus,
  customerCompanyAdminService,
} from "services/customerCompanyAdminService";
import { getThemeColor } from "theme/colors";
import { describeSchedule, formatDate } from "./format";

interface WorkflowsTabProps {
  customerCompanyId: string;
}

const STATUS_COLORS: Record<WorkflowExecutionStatus, string> = {
  Pending: "gray",
  Running: "blue",
  Completed: "green",
  Failed: "red",
  Cancelled: "orange",
  WaitingForApproval: "yellow",
};

/**
 * Every workflow belonging to any member of this company.
 *
 * Staff can rename, pause, delete and inspect runs, and can remove a schedule. They cannot create
 * a workflow or add/edit a schedule: both need the widget application token that is written into
 * the EventBridge payload and is not recoverable server-side, so those stay in the Customer
 * Portal. Deactivating a workflow does stop its scheduled runs — each skipped fire is recorded in
 * the run history as Cancelled.
 */
const WorkflowsTab = ({ customerCompanyId }: WorkflowsTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [workflows, setWorkflows] = useState<CustomerCompanyWorkflow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const [editing, setEditing] = useState<CustomerCompanyWorkflow | null>(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] =
    useState<CustomerCompanyWorkflow | null>(null);
  const [scheduleDeleteTarget, setScheduleDeleteTarget] = useState<{
    workflowId: string;
    scheduleId: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [logsFor, setLogsFor] = useState<CustomerCompanyWorkflow | null>(null);
  const [logs, setLogs] = useState<CustomerCompanyWorkflowLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setWorkflows(
        await customerCompanyAdminService.listWorkflows(customerCompanyId),
      );
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.customerCompanies.workflows.loadFailed", {
            defaultValue: "Failed to load workflows.",
          }),
      );
    } finally {
      setLoading(false);
    }
  }, [customerCompanyId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const reportFailure = (err: any) =>
    notify({
      type: "error",
      title: t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      description: err.response?.data?.error,
    });

  const replaceWorkflow = (updated: CustomerCompanyWorkflow) =>
    setWorkflows((prev) =>
      prev.map((w) => (w.id === updated.id ? { ...w, ...updated } : w)),
    );

  const handleToggleActive = async (
    workflow: CustomerCompanyWorkflow,
    isActive: boolean,
  ) => {
    setPendingId(workflow.id);
    try {
      const updated = await customerCompanyAdminService.updateWorkflow(
        customerCompanyId,
        workflow.id,
        { isActive },
      );
      replaceWorkflow({ ...workflow, ...updated });
      notify({
        type: "success",
        title: isActive
          ? t("app.customerCompanies.workflows.activated", {
              defaultValue: "Workflow activated.",
            })
          : t("app.customerCompanies.workflows.paused", {
              defaultValue: "Workflow paused. Scheduled runs will be skipped.",
            }),
      });
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setPendingId(null);
    }
  };

  const openEdit = (workflow: CustomerCompanyWorkflow) => {
    setEditing(workflow);
    setForm({ name: workflow.name, description: workflow.description });
    setFormError(null);
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!form.name.trim() || !form.description.trim()) {
      setFormError(
        t("app.customerCompanies.workflows.requiredFields", {
          defaultValue: "Name and description are required.",
        }),
      );
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const updated = await customerCompanyAdminService.updateWorkflow(
        customerCompanyId,
        editing.id,
        { name: form.name.trim(), description: form.description.trim() },
      );
      replaceWorkflow({ ...editing, ...updated });
      setEditing(null);
      notify({
        type: "success",
        title: t("app.customerCompanies.workflows.saved", {
          defaultValue: "Workflow saved.",
        }),
      });
    } catch (err: any) {
      setFormError(
        err.response?.data?.error ||
          t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await customerCompanyAdminService.deleteWorkflow(
        customerCompanyId,
        deleteTarget.id,
      );
      setWorkflows((prev) => prev.filter((w) => w.id !== deleteTarget.id));
      notify({
        type: "success",
        title: t("app.customerCompanies.workflows.deleted", {
          defaultValue: "Workflow deleted.",
        }),
      });
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!scheduleDeleteTarget) return;
    setDeleting(true);
    try {
      await customerCompanyAdminService.deleteWorkflowSchedule(
        customerCompanyId,
        scheduleDeleteTarget.workflowId,
        scheduleDeleteTarget.scheduleId,
      );
      setWorkflows((prev) =>
        prev.map((w) =>
          w.id === scheduleDeleteTarget.workflowId
            ? {
                ...w,
                schedules: w.schedules.filter(
                  (s) => s.id !== scheduleDeleteTarget.scheduleId,
                ),
              }
            : w,
        ),
      );
      notify({
        type: "success",
        title: t("app.customerCompanies.workflows.schedules.deleted", {
          defaultValue: "Schedule deleted.",
        }),
      });
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setDeleting(false);
      setScheduleDeleteTarget(null);
    }
  };

  const openLogs = async (workflow: CustomerCompanyWorkflow) => {
    setLogsFor(workflow);
    setLogs([]);
    setLogsLoading(true);
    try {
      setLogs(
        await customerCompanyAdminService.getWorkflowLogs(
          customerCompanyId,
          workflow.id,
        ),
      );
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setLogsLoading(false);
    }
  };

  const ownerLabel = (workflow: CustomerCompanyWorkflow) =>
    workflow.ownerName ?? workflow.ownerEmail ?? workflow.ownerExternalId;

  const cardProps = {
    borderWidth: "1px",
    borderColor: getThemeColor("card.border"),
    borderRadius: "md",
  } as const;

  return (
    <Card.Root {...cardProps}>
      <Card.Body>
        <Stack gap={3}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Stack gap={1}>
              <Heading size="sm">
                {t("app.customerCompanies.workflows.title", {
                  defaultValue: "Workflows",
                })}
              </Heading>
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.workflows.subtitle", {
                  defaultValue:
                    "Automations owned by this company's members. New workflows and schedules are created from the customer portal.",
                })}
              </Text>
            </Stack>
            <RefreshButton onClick={load} loading={loading} />
          </Flex>

          {error && (
            <InfoBox variant="error">
              <Text color="red.700">{error}</Text>
            </InfoBox>
          )}

          {loading ? (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.customerCompanies.workflows.loading", {
                defaultValue: "Loading workflows…",
              })}
            </Text>
          ) : workflows.length === 0 ? (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.customerCompanies.workflows.empty", {
                defaultValue: "No workflows for this company yet.",
              })}
            </Text>
          ) : (
            <Stack gap={3}>
              {workflows.map((workflow) => (
                <Stack
                  key={workflow.id}
                  gap={2}
                  borderWidth="1px"
                  borderColor={getThemeColor("card.border")}
                  borderRadius="md"
                  p={3}
                >
                  <Flex
                    justify="space-between"
                    align="flex-start"
                    gap={3}
                    wrap="wrap"
                  >
                    <Stack gap={1} flex={1} minW="240px">
                      <HStack gap={2} wrap="wrap">
                        <Text fontWeight="medium">{workflow.name}</Text>
                        {workflow.scope === "Company" ? (
                          <Badge colorPalette="blue" variant="subtle">
                            {t("app.customerCompanies.workflows.scopeCompany", {
                              defaultValue: "Company",
                            })}
                          </Badge>
                        ) : (
                          <Badge colorPalette="gray" variant="outline">
                            {t(
                              "app.customerCompanies.workflows.scopePersonal",
                              {
                                defaultValue: "Personal",
                              },
                            )}
                          </Badge>
                        )}
                        {!workflow.isActive && (
                          <Badge colorPalette="orange" variant="subtle">
                            {t("app.customerCompanies.workflows.pausedBadge", {
                              defaultValue: "Paused",
                            })}
                          </Badge>
                        )}
                        {workflow.ownerMemberId === null && (
                          <Badge colorPalette="red" variant="subtle">
                            {t("app.customerCompanies.workflows.ownerUnknown", {
                              defaultValue: "Owner not in this company",
                            })}
                          </Badge>
                        )}
                        {workflow.ownerIsDisabled && (
                          <Badge colorPalette="gray" variant="subtle">
                            {t(
                              "app.customerCompanies.workflows.ownerDisabled",
                              {
                                defaultValue: "Owner disabled",
                              },
                            )}
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="sm" color="var(--text-secondary-color)">
                        {workflow.description}
                      </Text>
                      <HStack gap={1}>
                        <MdPerson
                          size={14}
                          color="var(--text-secondary-color)"
                        />
                        {workflow.ownerMemberId ? (
                          <Button
                            intent="ghost"
                            onClick={() =>
                              navigate(
                                `/admin/customers/${workflow.ownerMemberId}`,
                              )
                            }
                          >
                            {ownerLabel(workflow)}
                          </Button>
                        ) : (
                          <Text
                            fontSize="xs"
                            fontFamily="mono"
                            color="var(--text-secondary-color)"
                          >
                            {workflow.ownerExternalId}
                          </Text>
                        )}
                      </HStack>
                    </Stack>

                    <HStack gap={1} align="center">
                      <Switch
                        checked={workflow.isActive}
                        disabled={pendingId === workflow.id}
                        onCheckedChange={(e: any) =>
                          handleToggleActive(workflow, e.checked)
                        }
                      />
                      <Button intent="ghost" onClick={() => openLogs(workflow)}>
                        <MdHistory />{" "}
                        {t("app.customerCompanies.workflows.logs.title", {
                          defaultValue: "Run history",
                        })}
                      </Button>
                      <Button intent="ghost" onClick={() => openEdit(workflow)}>
                        <MdEdit />{" "}
                        {t("app.common.edit", { defaultValue: "Edit" })}
                      </Button>
                      <Button
                        intent="ghost"
                        onClick={() => setDeleteTarget(workflow)}
                      >
                        <MdDelete />{" "}
                        {t("app.common.delete", { defaultValue: "Delete" })}
                      </Button>
                    </HStack>
                  </Flex>

                  {workflow.schedules.length > 0 && (
                    <Stack gap={1}>
                      <Text fontSize="xs" color="var(--text-secondary-color)">
                        {t("app.customerCompanies.workflows.schedules.title", {
                          defaultValue: "Schedules",
                        })}
                      </Text>
                      {workflow.schedules.map((schedule) => (
                        <HStack
                          key={schedule.id}
                          justify="space-between"
                          gap={2}
                          wrap="wrap"
                        >
                          <HStack gap={2}>
                            <Text fontSize="sm">
                              {describeSchedule(schedule)}
                            </Text>
                            {!schedule.isActive && (
                              <Badge colorPalette="gray" variant="subtle">
                                {t("app.common.inactive", {
                                  defaultValue: "Inactive",
                                })}
                              </Badge>
                            )}
                            {schedule.nextRunAt && (
                              <Text
                                fontSize="xs"
                                color="var(--text-secondary-color)"
                              >
                                {t(
                                  "app.customerCompanies.workflows.schedules.nextRun",
                                  {
                                    defaultValue: "Next run: {{when}}",
                                    when: formatDate(schedule.nextRunAt),
                                  },
                                )}
                              </Text>
                            )}
                          </HStack>
                          <Button
                            intent="ghost"
                            onClick={() =>
                              setScheduleDeleteTarget({
                                workflowId: workflow.id,
                                scheduleId: schedule.id,
                              })
                            }
                          >
                            <MdDelete />{" "}
                            {t("app.common.delete", { defaultValue: "Delete" })}
                          </Button>
                        </HStack>
                      ))}
                    </Stack>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Card.Body>

      {/* Edit workflow */}
      <DialogRoot
        open={editing !== null}
        onOpenChange={(e: { open: boolean }) =>
          e.open ? undefined : setEditing(null)
        }
        size="lg"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("app.customerCompanies.workflows.edit", {
                defaultValue: "Edit workflow",
              })}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={4}>
              {formError && (
                <InfoBox variant="error">
                  <Text color="red.700">{formError}</Text>
                </InfoBox>
              )}
              <Field
                label={t("app.customerCompanies.workflows.fields.name", {
                  defaultValue: "Name",
                })}
                required
              >
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  maxLength={255}
                />
              </Field>
              <Field
                label={t("app.customerCompanies.workflows.fields.description", {
                  defaultValue: "Description",
                })}
                helperText={t(
                  "app.customerCompanies.workflows.fields.descriptionHelp",
                  {
                    defaultValue:
                      "This text is sent to the assistant as the instruction each time the workflow runs.",
                  },
                )}
                required
              >
                <Textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={5}
                />
              </Field>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button intent="secondary" onClick={() => setEditing(null)}>
              {t("app.common.cancel")}
            </Button>
            <Button intent="primary" onClick={handleSave} disabled={saving}>
              {saving
                ? t("app.common.saving", { defaultValue: "Saving..." })
                : t("app.common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      {/* Run history */}
      <DialogRoot
        open={logsFor !== null}
        onOpenChange={(e: { open: boolean }) =>
          e.open ? undefined : setLogsFor(null)
        }
        size="xl"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("app.customerCompanies.workflows.logs.title", {
                defaultValue: "Run history",
              })}
              {logsFor ? ` — ${logsFor.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {logsLoading ? (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.workflows.logs.loading", {
                  defaultValue: "Loading run history…",
                })}
              </Text>
            ) : logs.length === 0 ? (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.workflows.logs.empty", {
                  defaultValue: "This workflow has not run yet.",
                })}
              </Text>
            ) : (
              <Box overflowX="auto">
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>
                        {t("app.customerCompanies.workflows.logs.startedAt", {
                          defaultValue: "Started",
                        })}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.customerCompanies.workflows.logs.status", {
                          defaultValue: "Status",
                        })}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.customerCompanies.workflows.logs.completedAt", {
                          defaultValue: "Completed",
                        })}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.customerCompanies.workflows.logs.details", {
                          defaultValue: "Details",
                        })}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {logs.map((log) => (
                      <Table.Row key={log.id}>
                        <Table.Cell>{formatDate(log.startedAt)}</Table.Cell>
                        <Table.Cell>
                          <Badge
                            colorPalette={STATUS_COLORS[log.status] ?? "gray"}
                            variant="subtle"
                          >
                            {log.status}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>{formatDate(log.completedAt)}</Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs">{log.errorMessage ?? "—"}</Text>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </DialogBody>
          <DialogFooter>
            <Button intent="secondary" onClick={() => setLogsFor(null)}>
              {t("app.common.close", { defaultValue: "Close" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      <DeleteConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteWorkflow}
        loading={deleting}
        title={t("app.customerCompanies.workflows.deleteTitle", {
          defaultValue: "Delete workflow",
        })}
        message={t("app.customerCompanies.workflows.deleteConfirm", {
          defaultValue:
            "The workflow, its schedules and its run history will be removed. To stop it without deleting, pause it instead.",
        })}
      />

      <DeleteConfirmDialog
        isOpen={scheduleDeleteTarget !== null}
        onClose={() => setScheduleDeleteTarget(null)}
        onConfirm={handleDeleteSchedule}
        loading={deleting}
        title={t("app.customerCompanies.workflows.schedules.deleteTitle", {
          defaultValue: "Delete schedule",
        })}
        message={t("app.customerCompanies.workflows.schedules.deleteConfirm", {
          defaultValue:
            "This recurrence will stop. It can only be recreated from the customer portal.",
        })}
      />
    </Card.Root>
  );
};

export default WorkflowsTab;
